import { modelOptions, mongoose, PropType, post, prop, Ref } from "@typegoose/typegoose";
import { z } from "zod";
import { NodeId, NodeIdString, ObjectIdString } from "../../../../validators/index.js";
import { getZodSchemaFromClass, VerificationStatusString, validate } from "../../../../zod-decorator/index.js";
import { Organization } from "../../../organization/organization.js";
import { addSchemaKeyHook, getDefaultSchemaOptions, SchemaBase, SchemaConstructorArgs } from "../../../schemaBase.js";
import type {
    SproutVerificationInstanceFieldsWithDefaults,
    SproutVerificationInstanceSchemaTypes,
} from "../../../types/sproutVerificationInstance.d.js";
import type { DtoValidatorTypes } from "../../../validatableClass.js";
import { VerificationStatus } from "./generalSproutVerificationCheck.js";
import { getMockPresets, SproutVerificationInstanceMockPreset } from "./mocks.js";
import { RecentVerificationDataPoint } from "./recentVerificationDataPoint.js";
import { SproutVerificationChecks } from "./sproutVerificationChecks.js";

export { SproutVerificationInstanceMockPreset } from "./mocks.js";

const SCHEMA_KEY = "SproutVerificationInstance" as const;

/**
 * Complete verification record for a specific valve state
 *
 * This model represents a single verification attempt that validates whether an irrigation
 * event occurred as intended. Each instance contains:
 * - Event context (device, timing, intended valve states)
 * - Verification results and status
 * - Detailed sensor data and measurements
 * - Comprehensive checks for different system components
 *
 * The verification process examines multiple aspects:
 * - Valve state correctness
 * - Device connectivity and timing
 * - Sensor readings validation
 * - Solenoid operation verification
 *
 * @see SproutVerificationChecks - Detailed component-specific verification results
 * @see RecentVerificationDataPoint - Sensor data used during verification
 */
// TODO: convert to responseOnly schema class
@modelOptions({
    schemaOptions: {
        ...getDefaultSchemaOptions(SCHEMA_KEY),
        collection: "sproutverificationmodels",
        timestamps: true,
        _id: true,
    },
})
@post<SchemaBase<SproutVerificationInstance>>(...addSchemaKeyHook(SCHEMA_KEY))
export class SproutVerificationInstance extends SchemaBase<
    SproutVerificationInstance,
    SproutVerificationInstanceFieldsWithDefaults
> {
    static readonly schemaKey = SCHEMA_KEY;

    @validate({ schemaKey: SCHEMA_KEY })
    readonly schemaKey = SCHEMA_KEY;

    @validate({ zodValidator: ObjectIdString, responseOnly: true })
    public _id!: mongoose.Types.ObjectId;

    /** Organization who owns the device being verified */
    @validate({ zodValidator: ObjectIdString, responseOnly: true })
    @prop({ type: () => mongoose.Types.ObjectId, required: true, ref: "Organization" })
    public owner!: Ref<Organization>;

    /** Timestamp when this verification record was created */
    @validate({ zodValidator: z.coerce.date(), responseOnly: true })
    @prop({ type: () => Date, required: true })
    public date!: Date;

    /** node_id of the device being verified */
    @validate({ zodValidator: NodeIdString, responseOnly: true })
    @prop({ type: () => String, required: true })
    public device!: NodeId;

    /**
     * True means that one or more of the valves was supposed to open, false means all valves were supposed to be closed
     */
    @validate({ zodValidator: z.boolean(), responseOnly: true })
    @prop({ type: () => Boolean, required: true })
    public openVerification!: boolean;

    /**
     * If the valves are still in an edge case. About to open or just closed or something and
     * don't exactly match the schedule
     */
    @validate({ zodValidator: z.boolean(), responseOnly: true })
    @prop({ type: () => Boolean, required: true })
    public someUncertainty!: boolean;

    /** Array indicating which valves were intended to be open [valve0, valve1] */
    @validate({ zodValidator: z.array(z.boolean()), responseOnly: true })
    @prop({ type: () => Boolean, required: true }, PropType.ARRAY)
    public valvesIntendedOpen!: boolean[];

    /**
     * The range of time over which the transition could
     * be expected to be made. Both items will be the same
     * unless there was a last minute schedule change.
     */
    @validate({ zodValidator: z.array(z.coerce.date()).optional(), responseOnly: true })
    @prop({ type: () => Date, required: false }, PropType.ARRAY)
    public acceptableTransitionDateRange?: Date[];

    /**
     * Timestamp of the last valve state change on this device (open to closed or closed to open),
     * independent of when the valve was supposed to transition.
     */
    @validate({ zodValidator: z.coerce.date(), responseOnly: true })
    @prop({ type: () => Date, required: true })
    public valveTransitionDate!: Date;

    /** Last time the device contacted the server before this verification */
    @validate({ zodValidator: z.coerce.date(), responseOnly: true })
    @prop({ type: () => Date, required: true })
    public lastDeviceContact!: Date;

    /** Actual valve states reported by the device [valve0, valve1] */
    @validate({ zodValidator: z.array(z.boolean()), responseOnly: true })
    @prop({ type: () => Boolean, required: true }, PropType.ARRAY)
    public reportedValvesOpen!: boolean[];

    /** Whether the valve was in manual override mode when this verification was created */
    @validate({ zodValidator: z.boolean().optional(), responseOnly: true })
    @prop({ type: () => Boolean, required: false })
    public manualOverrideEngaged?: boolean;

    /** Sensor measurements collected during this verification period */
    @validate({
        childClass: RecentVerificationDataPoint,
        childClassTransform: (object) => z.array(object),
        responseOnly: true,
    })
    @prop({ type: () => RecentVerificationDataPoint, required: true }, PropType.ARRAY)
    public dataMeasured!: RecentVerificationDataPoint[];

    /**
     * Is the thing being verified ongoing? i.e. is this still the current valve state?
     */
    @validate({ zodValidator: z.boolean(), responseOnly: true })
    @prop({ type: () => Boolean, required: true })
    public inProgress!: boolean;

    /**
     * Are we still waiting for data on the situation?
     */
    @validate({ zodValidator: z.boolean(), responseOnly: true })
    @prop({ type: () => Boolean, required: true })
    public pendingData!: boolean;

    /** Percentage of verification period where valves were in correct state */
    @validate({ zodValidator: z.number().optional(), responseOnly: true })
    @prop({ type: () => Number, required: false })
    public percentageOfTimeInCorrectState?: number;

    /** Percentage of verification period where sensor readings were acceptable */
    @validate({ zodValidator: z.number().optional(), responseOnly: true })
    @prop({ type: () => Number, required: false })
    public percentageOfTimeWithCorrectSensorReadings?: number;

    /** Overall success status of this verification attempt */
    @validate({ zodValidator: z.boolean(), responseOnly: true })
    @prop({ type: () => Boolean, required: true })
    public successful!: boolean;

    /** Human-readable explanation if verification failed */
    @validate({ zodValidator: z.string().optional(), responseOnly: true })
    @prop({ type: () => String, required: false })
    public reasonForFailure?: string;

    /** Current verification status (successful, pending, error, etc.) */
    @validate({ zodValidator: VerificationStatusString.optional(), responseOnly: true })
    @prop({ type: () => String, required: false })
    public status?: VerificationStatus;

    /** Detailed verification results for individual system components */
    @validate({ childClass: SproutVerificationChecks, responseOnly: true })
    @prop({ type: () => SproutVerificationChecks, required: true })
    public verificationChecks!: SproutVerificationChecks;

    static getters = {} as const satisfies Record<string, (self: SproutVerificationInstance) => any>;

    static utilities = {
        generateMock: ({
            preset,
            owner,
            device,
        }: {
            preset: SproutVerificationInstanceMockPreset;
            owner: Ref<Organization>;
            device: string;
        }): SproutVerificationInstance =>
            new SproutVerificationInstance({
                ...getMockPresets()[preset],
                owner,
                device,
            }),
    } as const satisfies Record<string, (...args: any) => any>;

    static dtoValidators: DtoValidatorTypes<SproutVerificationInstanceSchemaTypes>;

    constructor(
        properties?: SchemaConstructorArgs<SproutVerificationInstance, SproutVerificationInstanceFieldsWithDefaults>,
    ) {
        super(properties);
        Object.assign(this, properties);
    }
}

SproutVerificationInstance.dtoValidators = getZodSchemaFromClass(
    SproutVerificationInstance,
) as unknown as DtoValidatorTypes<SproutVerificationInstanceSchemaTypes>;
